import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/auth";
import ReferralCard from "@/components/ReferralCard";

/* ============================================================
 /my-tracks — Producer dashboard. Every track you've dropped,
 its current chart score, judge score if it's been reviewed,
 and one-tap delete for tracks you regret.
 ============================================================ */

const STYLE_LABEL = {
 aggressive: "AGGRESSIVE", boom_bap: "BOOM BAP", trap: "TRAP",
 smooth: "SMOOTH", conscious: "CONSCIOUS",
};

export default function MyTracks() {
 const { user } = useAuth();
 const [tracks, setTracks] = useState([]);
 const [loading, setLoading] = useState(true);

 const load = async () => {
 setLoading(true);
 try {
 const { data } = await axios.get(`${API}/tracks/mine`);
 setTracks(data || []);
 } catch (e) {
 setTracks([]);
 }
 setLoading(false);
 };

 useEffect(() => { if (user) load(); }, [user]);

 const del = async (id) => {
 if (!window.confirm("Delete this track permanently? Votes and comments will be wiped too.")) return;
 try {
 await axios.delete(`${API}/tracks/${id}`);
 setTracks(ts => ts.filter(t => t.id !== id));
 } catch (e) {}
 };

 if (!user) {
 return (
 <div className="max-w-3xl mx-auto p-10 text-center">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-4"> SIGN IN REQUIRED</div>
 <Link to="/login" className="text-yt-yellow underline">Sign in</Link> to view your track dashboard.
 </div>
 );
 }

 const totalScore = tracks.reduce((s, t) => s + (t.score || 0), 0);
 const readyCount = tracks.filter(t => t.status === "ready").length;

 return (
 <div className="max-w-5xl mx-auto px-6 md:px-10 py-12">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-2"> PRODUCER DASHBOARD</div>
 <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.9] mb-2">
 MY <span className="text-yt-yellow">TRACKS</span>
 </h1>
 <p className="font-mono text-xs text-yt-muted tracking-widest mb-8">
 {readyCount} LIVE · {totalScore >= 0 ? "+" : ""}{totalScore} NET SCORE
 </p>

 <div className="mb-8">
 <Link to="/charts/submit" data-testid="mytracks-new"
 className="inline-block bg-yt-yellow text-black font-display text-lg tracking-widest px-5 py-3 shadow-brutal hover:bg-white">
 ▶ DROP A NEW TRACK
 </Link>
 </div>

 {/* Growth loop — every user's referral link + progress */}
 <ReferralCard />

 {loading && <div className="font-mono text-yt-muted animate-pulse-red">LOADING...</div>}

 {!loading && tracks.length === 0 && (
 <div className="border-2 border-yt-border bg-yt-surface p-10 text-center">
 <div className="font-display text-3xl mb-2">NO TRACKS YET</div>
 <p className="font-mono text-sm text-yt-muted mb-6">Every legend starts at zero. Drop your first bar.</p>
 <Link to="/charts/submit" data-testid="mytracks-first"
 className="inline-block bg-yt-yellow text-black font-display text-lg tracking-widest px-6 py-3 shadow-brutal">
 ▶ DROP FIRST TRACK
 </Link>
 </div>
 )}

 <div className="space-y-3">
 {tracks.map(t => (
 <div key={t.id} data-testid={`mytrack-${t.id}`}
 className="bg-yt-surface border-2 border-yt-border hover:border-yt-yellow transition-colors p-4 md:p-5">
 <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
 <div className="flex-1 min-w-0">
 <div className="font-mono text-[9px] tracking-widest text-yt-yellow mb-1">
 {STYLE_LABEL[t.style] || t.style?.toUpperCase()}
 {t.anonymous && <span className="text-yt-muted"> · ANON</span>}
 </div>
 <Link to={`/tracks/${t.id}`}
 className="font-display text-xl md:text-2xl hover:text-yt-yellow transition-colors">
 {t.title}
 </Link>
 </div>
 <div className="flex items-center gap-2">
 {t.status === "generating" && (
 <span className="font-mono text-[10px] tracking-widest text-yt-yellow animate-pulse-red"> RENDERING...</span>
 )}
 {t.status === "failed" && (
 <span className="font-mono text-[10px] tracking-widest text-yt-red">✕ FAILED</span>
 )}
 {t.status === "ready" && t.audio_url && (
 <audio data-testid={`mytrack-audio-${t.id}`} src={t.audio_url} controls preload="none" className="max-w-[280px]" />
 )}
 </div>
 </div>
 <div className="flex items-center gap-4 font-mono text-xs">
 <span className={t.score > 0 ? "text-yt-judge" : t.score < 0 ? "text-yt-red" : "text-white"}>
 SCORE {t.score >= 0 ? "+" : ""}{t.score}
 </span>
 <span className="text-yt-judge">▲ {t.upvotes}</span>
 <span className="text-yt-red">▼ {t.downvotes}</span>
 {t.judge_score != null && (
 <span className="text-yt-yellow"> JUDGE {t.judge_score}/100</span>
 )}
 <div className="flex-1" />
 <button onClick={() => del(t.id)}
 data-testid={`mytrack-del-${t.id}`}
 className="text-yt-muted hover:text-yt-red underline">
 delete
 </button>
 </div>
 </div>
 ))}
 </div>
 </div>
 );
}
